import { replace, toPairs } from 'lodash';
import { generatePath } from 'react-router-dom';
export const PatientRoutePrefix = '/patient';
export const ClinicianRoutePrefix = '/clinician';

const appendReturnTo = (path: string, returnTo?: string) => {
  if (returnTo) {
    return `${path}?returnTo=${encodeURIComponent(returnTo)}`;
  }
  return path;
};

export const paths = {
  signIn: () => '/sign-in',
  clinician: {
    home: () => generatePath(Routes.ClinicianHome),
    patient: {
      assessments: (patientId: string) =>
        `/${generatePath(
          [PatientRoutePrefix, patientId, routes.clinician.patient.assessments]
            .map(_ => _.replace('/', ''))
            .join('/'),
          { patientId }
        )}`
    },
    billing: {
      buy: {
        bulk: (returnTo?: string) =>
          appendReturnTo(generatePath(ClinicRoutes.ClinicBuyBulk), returnTo),
        ppu: (returnTo?: string) =>
          appendReturnTo(generatePath(ClinicRoutes.ClinicPayAsYouGo), returnTo)
      },
      checkout: () => generatePath(ClinicRoutes.ClinicCheckout)
    },
    billingSettings: () => generatePath(ClinicRoutes.ClinicBillingSettings),
    billingSubscription: () => generatePath(ClinicRoutes.ClinicBillingSubscription),
    organisationUsage: () =>
      generatePath(ClinicRoutes.ClinicBillingOrganisationUsage)
  },
  patient: {
    questionnaire: {
      injury: (patientId: string) =>
        [PatientRoutePrefix, patientId, PatientRoutes.QuestionnaireInjury]
          .map(_ => _.replace('/', ''))
          .join('/'),
      demographic: (patientId: string) =>
        [PatientRoutePrefix, patientId, PatientRoutes.QuestionnaireDemographics]
          .map(_ => _.replace('/', ''))
          .join('/')
    },
    home: (patientId: string) =>
      generatePath(
        [PatientRoutePrefix, ':patientId', PatientRoutes.PatientDashboard].join(
          '/'
        ),
        { patientId }
      ),
    assessments: (patientId: string) =>
      generatePath(
        [
          PatientRoutePrefix,
          ':patientId',
          routes.clinician.patient.assessments.replace('/', '')
        ].join('/'),
        { patientId }
      )
  },
  assessmentLink: () => [Routes.AssessmentLinkEntrypoint].join('/'),
  integration: {
    impact: {
      join: () => generatePath(IntegrationRoutes.IMPACT_JOIN),
      addPatient: () => generatePath(IntegrationRoutes.IMPACT_PATIENT_IMPORT)
    }
  },
  onboarding: {
    landing: () => '/onboarding',
    registration: () => '/onboarding/registration',
    setup: (orgId: string) => `/onboarding/setup/${orgId}`
  }
};

/**
 * The new route config for the CRx router
 */
export const routes = {
  clinician: {
    patient: {
      assessments:
        '/assessments' /* TODO: Replace with '/patient/:patientId/assessments' when routing is refactored */
    }
  },
  admin: {
    organisationManagement: '/admin/organisation-management',
    clinicSignupLinkGeneration: '/admin/clinic-link-generation'
  },
  onboarding: {
    landing: '/onboarding',
    registration: '/onboarding/registration',
    setup: '/onboarding/setup/:orgId'
  }
} as const;

export enum PatientRoutes {
  PatientOnboardingProfile = '/onboarding/profile',
  Home = '/home',
  PatientDemographics = '/demographics',
  PatientHowAreYouTracker = '/how-are-you',
  PatientDemographicsProfile = '/demographics/profile',
  PatientDemographicsInjury = '/demographics/injury',
  PatientDemographicsPersonalHistory = '/demographics/personal-history',
  PatientSymptom = '/symptom/:symptom',
  PatientSsoInfo = '/symptom/:symptom/info',
  PatientCgs = '/outcome-measures/cgs',
  AssessmentStart = '/outcome-measures',
  AssessmentResume = '/outcome-measures/progress',
  AssessmentControlFlow = '/questionnaireFlow',
  AssessmentComplete = '/outcome-measures/complete',
  PatientDhi = '/outcome-measures/dhi',
  PatientPcs = '/outcome-measures/pcs',
  PatientPromis = '/outcome-measures/promis',
  QuestionnaireDemographics = '/outcome-measures/demographics',
  QuestionnaireInjury = '/outcome-measures/injury',
  PatientCreateAccount = '/create_account',
  PatientDashboard = '/dashboard',
  PatientDashboardReport = '/dashboard/report',
  CompletionPromis = '/completion/promis',
  Completion = '/completion/all',
  ImpactAPI = '/impactAPI',
  PatientTreatmentRecommendations = '/treatment-recommendations',
  PatientTreatmentRecommendationsV2 = '/treatment-recommendations-v2',
  PatientTreatmentRecommendationsExportPDF = '/treatment-recommendations/exportPDF',
  PatientSubtypeRange = '/analysis/subtype-range',
  PatientInventoryItemAnalysis = '/analysis/inventory'
}

export const getPatientRouteUrl = (route: PatientRoutes) =>
  PatientRoutePrefix + route;

export enum ClinicRoutes {
  ClinicUserManagement = '/clinician/users',
  ClinicInfoEdit = '/clinician/clinic/info/edit',
  CrxCompanion = '/clinician/companion',
  ClinicBuyBulk = '/clinician/billing/buy/bulk',
  ClinicPayAsYouGo = '/clinician/billing/buy/ppu',
  ClinicCheckout = '/clinician/billing/buy/checkout',
  ClinicBillingSettings = '/clinician/billing/settings',
  ClinicBillingSubscription = '/clinician/billing/subscription',
  ClinicBillingOrganisationUsage = '/clinician/billing/organisation-usage'
}

export enum Routes {
  ClinicianHome = '/clinician/home',
  ClinicianPatientList = '/clinician/patients',
  ClinicianAssessment = '/clinician/patient/:patientId/assessment',
  ClinicianPatientChart = '/clinician/patient/:patientId/chart',
  ClinicianPatientDashboard = '/clinician/patient/:patientId/dashboard',
  ClinicianPatientEducationalArticles = '/clinician/patient/:patientId/educational-articles',
  ClinicianPatientExercisesLibrary = '/clinician/patient/:patientId/exercises-library',
  ClinicianPatientGoalsLibrary = '/clinician/patient/:patientId/goals-library',
  ClinicianPatientProgress = '/clinician/patient/:patientId/progress',
  ClinicianPatientProtocolsAndProgressions = '/clinician/patient/:patientId/protocols-and-progressions',
  ClinicianPatientSymptoms = '/clinician/patient/:patientId/symptoms',
  ClinicianPatientSymptom = '/clinician/patient/:patientId/symptom/:symptom',
  ClinicianPatientHistory = '/clinician/patient/:patientId/history',
  ClinicianPatientInjury = '/clinician/patient/:patientId/injury',
  ClinicianPatientProfile = '/clinician/patient/:patientId/profile',
  ClinicianRecoveryRx = '/clinician/patient/:patientId/recovery-rx',
  ClinicianTreatmentPlanning = '/clinician/patient/:patientId/treatment-planning',
  ClinicianTreatmentPlanningGoals = '/clinician/patient/:patientId/treatment-planning/goals',

  ClinicianCreateAccount = '/clinician/create_account',
  VerifyEmail = '/account/verify',
  ForgotPassword = '/password/reset',
  TermsOfService = '/terms-of-service',
  PrivacyPolicy = '/privacy-policy',
  SignInSuffix = '/sign-in',
  CreateAccountSuffix = '/create-account',
  AssessmentLinkEntrypoint = '/assessment-link/:token'
}

export enum IntegrationRoutes {
  Prefix = '/integration',
  IMPACT = '/integration/impact',
  IMPACT_LANDING = '/integration/impact/landing',
  IMPACT_LANDING_SIGNIN = '/integration/impact/signin',
  IMPACT_JOIN = '/integration/impact/join',
  IMPACT_PATIENT_IMPORT = '/integration/impact/add-patient'
}

export const buildUrl = (route: string, params: Record<string, any>) =>
  toPairs(params).reduce(
    (route, [key, value]) =>
      replace(route, new RegExp(`/:(${key})(/|$)`, 'g'), `/${value}$2`),
    route as string
  );

export interface ClinicianQueryParams {
  patientId: string;
}

export interface SymptomQueryParams {
  symptom: string;
}
